import { useCallback, useEffect, useRef, useState } from "react";
import { LoaderCircle, Play, Square } from "lucide-react";
import API_BASE_URL from "../config";
import "./LoadTestPage.css";

/** Admin-only testing page — two modes sharing one form/log/results shell:
 *
 * - Load test: triggers backend/app/api/load_test.py::stream_load_test
 *   (--runner direct, in-process against the app's own live qa_system
 *   singleton) — N concurrent simulated users, throughput/latency/realized
 *   concurrency. Deliberately no --runner http option here — see that
 *   endpoint's own docstring for why; full HTTP-mode load testing stays a
 *   CLI-only capability (`python -m harness.load_test --runner http`).
 * - Accuracy test: triggers stream_accuracy_test — the same run_direct +
 *   grade_case loop harness/cli.py uses, one question at a time, streaming
 *   a per-question result as it completes.
 *
 * Both accept either a preset question set (golden/arabic_legal) or a
 * client-side-uploaded JSON file of questions — read and validated in the
 * browser (file.text() + JSON.parse()), sent as a normal field in the same
 * POST body that starts the SSE stream (not a separate upload endpoint;
 * this is small structured data, not a binary blob).
 */
export default function LoadTestPage() {
  const [testType, setTestType] = useState("load"); // "load" | "accuracy"
  const [questionSource, setQuestionSource] = useState("preset"); // "preset" | "upload"

  const [dataset, setDataset] = useState("golden");
  const [database, setDatabase] = useState("Baseline");
  const [users, setUsers] = useState(5);
  const [requestsPerUser, setRequestsPerUser] = useState(1);
  const [rampUp, setRampUp] = useState(0);
  const [judgeMode, setJudgeMode] = useState("off");

  const [customQuestions, setCustomQuestions] = useState(null);
  const [customQuestionsFileName, setCustomQuestionsFileName] = useState("");
  const [customQuestionsError, setCustomQuestionsError] = useState("");

  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [caseResults, setCaseResults] = useState([]);
  const [error, setError] = useState("");

  const abortControllerRef = useRef(null);
  const logEndRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [log]);

  const appendLog = useCallback((entry) => {
    setLog((prev) => [...prev, entry]);
  }, []);

  const switchTestType = (nextType) => {
    if (nextType === testType) return;
    setTestType(nextType);
    setSummary(null);
    setCaseResults([]);
    setLog([]);
    setError("");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setCustomQuestionsError("");
    setCustomQuestions(null);
    setCustomQuestionsFileName("");
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed)) throw new Error("File must contain a JSON array of question objects.");
      if (parsed.length === 0) throw new Error("File contains zero questions.");
      const badIndex = parsed.findIndex(
        (q) => !q || typeof q !== "object" || typeof q.question !== "string" || !q.question.trim(),
      );
      if (badIndex !== -1) {
        throw new Error(`Entry ${badIndex + 1} is missing a non-empty "question" string.`);
      }
      setCustomQuestions(parsed);
      setCustomQuestionsFileName(file.name);
    } catch (err) {
      setCustomQuestionsError(err.message || "Could not read/parse this file.");
    }
  };

  const handleEvent = useCallback(
    (evt) => {
      if (evt.type === "status") {
        appendLog({ kind: "status", text: evt.message });
      } else if (evt.type === "request_done") {
        appendLog({
          kind: evt.success ? "success" : "failure",
          text: `user ${evt.user_id} · ${evt.case_id} · ${evt.elapsed.toFixed(2)}s${
            evt.success ? "" : ` · ${evt.error}`
          }`,
        });
      } else if (evt.type === "case_done") {
        setCaseResults((prev) => [...prev, evt]);
        appendLog({
          kind: evt.overall_pass ? "success" : "failure",
          text: `${evt.id} · ${evt.group} · ${evt.overall_pass ? "PASS" : "FAIL"} · ${evt.elapsed.toFixed(1)}s`,
        });
      } else if (evt.type === "summary") {
        setSummary(evt);
        appendLog({ kind: "status", text: evt.saved_as ? `Done. Saved as ${evt.saved_as}` : "Done." });
      } else if (evt.type === "error") {
        setError(evt.message);
        appendLog({ kind: "failure", text: evt.message });
      }
    },
    [appendLog],
  );

  const handleRun = async () => {
    setRunning(true);
    setLog([]);
    setSummary(null);
    setCaseResults([]);
    setError("");
    abortControllerRef.current = new AbortController();

    const usingUpload = questionSource === "upload";
    const endpoint = testType === "accuracy" ? "/api/v1/load-test/accuracy-stream" : "/api/v1/load-test/stream";
    const body = testType === "accuracy"
      ? {
          dataset, database, judge_mode: judgeMode,
          ...(usingUpload ? { custom_questions: customQuestions } : {}),
        }
      : {
          dataset, database, users: Number(users), requests_per_user: Number(requestsPerUser),
          ramp_up: Number(rampUp),
          ...(usingUpload ? { custom_questions: customQuestions } : {}),
        };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";
        for (const block of blocks) {
          for (const line of block.split("\n")) {
            if (line.startsWith("data: ")) {
              try {
                handleEvent(JSON.parse(line.slice(6)));
              } catch {
                /* skip malformed */
              }
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message);
        appendLog({ kind: "failure", text: err.message });
      }
    } finally {
      setRunning(false);
    }
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
  };

  const runDisabled = running || (questionSource === "upload" && (!customQuestions || !!customQuestionsError));

  return (
    <div className="ds-page loadtest-page">
      <div className="ds-page-header">
        <h2 className="ds-page-title">{testType === "accuracy" ? "Accuracy test" : "Load test"}</h2>
        <p className="ds-page-subtitle">
          {testType === "accuracy"
            ? "Run a set of questions one at a time against the live QA pipeline and score each answer " +
              "(retrieval/context/answer/source), same scoring harness/cli.py uses, plus time-to-first-token " +
              "and tokens/sec per question."
            : "Simulate N concurrent users, each firing a sequential run of questions, and watch throughput, " +
              "latency, and realized concurrency in real time. Runs in-process against the live QA pipeline " +
              "(same code path real chat requests use) — it will compete with real traffic for GPU/DB resources."}
        </p>
      </div>

      {error && <div className="ds-alert ds-alert--danger">{error}</div>}

      <div className="loadtest-type-toggle">
        <ToggleChip active={testType === "load"} onClick={() => switchTestType("load")} disabled={running}>
          Load test
        </ToggleChip>
        <ToggleChip active={testType === "accuracy"} onClick={() => switchTestType("accuracy")} disabled={running}>
          Accuracy test
        </ToggleChip>
      </div>

      <div className="ds-card ds-toolbar loadtest-form">
        <div className="ds-field loadtest-source-toggle">
          <label>Question source</label>
          <div className="loadtest-source-buttons">
            <ToggleChip
              active={questionSource === "preset"} onClick={() => setQuestionSource("preset")} disabled={running}
            >
              Preset
            </ToggleChip>
            <ToggleChip
              active={questionSource === "upload"} onClick={() => setQuestionSource("upload")} disabled={running}
            >
              Upload JSON
            </ToggleChip>
          </div>
        </div>

        {questionSource === "preset" ? (
          <div className="ds-field">
            <label htmlFor="lt-dataset">Question set</label>
            <select
              id="lt-dataset" className="ds-select loadtest-control" value={dataset}
              onChange={(e) => setDataset(e.target.value)} disabled={running}
            >
              <option value="golden">Golden (engineering)</option>
              <option value="arabic_legal">Arabic legal</option>
            </select>
          </div>
        ) : (
          <div className="ds-field loadtest-upload-field">
            <label htmlFor="lt-upload">Questions file (.json)</label>
            <input
              id="lt-upload" type="file" accept=".json,application/json"
              onChange={handleFileChange} disabled={running}
            />
            {customQuestionsFileName && !customQuestionsError && (
              <span className="ds-help loadtest-file-name">
                {customQuestionsFileName} · {customQuestions?.length ?? 0} question(s)
              </span>
            )}
            {customQuestionsError && <span className="ds-help loadtest-file-error">{customQuestionsError}</span>}
          </div>
        )}

        <div className="ds-field">
          <label htmlFor="lt-database">Workspace</label>
          <select
            id="lt-database" className="ds-select loadtest-control" value={database}
            onChange={(e) => setDatabase(e.target.value)} disabled={running}
          >
            <option value="Lucking">Lücking</option>
            <option value="Betonbau">Betonbau</option>
            <option value="Tchalabi">Tchalabi</option>
            <option value="Baseline">Baseline</option>
            <option value="ArabicLegal">Arabic Legal</option>
          </select>
        </div>

        {testType === "load" ? (
          <>
            <div className="ds-field">
              <label htmlFor="lt-users">Concurrent users</label>
              <input
                id="lt-users" className="ds-input loadtest-control" type="number" min={1} value={users}
                disabled={running} onChange={(e) => setUsers(e.target.value)}
              />
            </div>
            <div className="ds-field">
              <label htmlFor="lt-rpu">Requests / user</label>
              <input
                id="lt-rpu" className="ds-input loadtest-control" type="number" min={1} value={requestsPerUser}
                disabled={running} onChange={(e) => setRequestsPerUser(e.target.value)}
              />
            </div>
            <div className="ds-field">
              <label htmlFor="lt-rampup">Ramp-up (s)</label>
              <input
                id="lt-rampup" className="ds-input loadtest-control" type="number" min={0} step="0.5"
                value={rampUp} disabled={running}
                onChange={(e) => setRampUp(e.target.value)}
                title="0 = every user's first request fires at the same instant"
              />
            </div>
          </>
        ) : (
          <div className="ds-field">
            <label htmlFor="lt-judge">Judge</label>
            <select
              id="lt-judge" className="ds-select loadtest-control" value={judgeMode}
              onChange={(e) => setJudgeMode(e.target.value)} disabled={running}
            >
              <option value="off">Off (keyword only)</option>
              <option value="rubric">Rubric</option>
              <option value="reference">Reference</option>
            </select>
          </div>
        )}

        {running ? (
          <button className="ds-btn ds-btn--danger" onClick={handleCancel}>
            <Square size={14} />
            Cancel
          </button>
        ) : (
          <button className="ds-btn ds-btn--primary" onClick={handleRun} disabled={runDisabled}>
            <Play size={14} />
            {testType === "accuracy" ? "Run accuracy test" : "Run load test"}
          </button>
        )}
      </div>

      {testType === "accuracy"
        ? (summary || caseResults.length > 0) && <AccuracyResultsPanel summary={summary} caseResults={caseResults} />
        : summary && <ResultsPanel summary={summary} />}

      {log.length > 0 && (
        <div className="loadtest-log">
          <div className="loadtest-log-header">
            {running && <LoaderCircle size={13} className="ds-spin" />}
            Log
          </div>
          <div className="loadtest-log-body">
            {log.map((entry, i) => (
              <div key={i} className={`loadtest-log-line loadtest-log-${entry.kind}`}>
                {entry.text}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Segmented single-select toggle, rendered as a design-system chip. */
function ToggleChip({ active, onClick, disabled, children }) {
  return (
    <button type="button" className="ds-chip" aria-pressed={active} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function PerfStatsRow({ label, stats, sampleSize, decimals = 2 }) {
  if (!sampleSize || !stats) return null;
  return (
    <div className="loadtest-latency">
      <span className="ds-label">{label}</span>
      <span>min {stats.min.toFixed(decimals)}</span>
      <span>median {stats.median.toFixed(decimals)}</span>
      <span>mean {stats.mean.toFixed(decimals)}</span>
      <span>p95 {stats.p95.toFixed(decimals)}</span>
      <span>p99 {stats.p99.toFixed(decimals)}</span>
      <span>max {stats.max.toFixed(decimals)}</span>
      <span className="loadtest-sample-size">(n={sampleSize})</span>
    </div>
  );
}

function ResultsPanel({ summary }) {
  const lat = summary.latency_successful;
  const concurrencyLow = summary.realized_concurrency < summary.requested_concurrency;

  return (
    <div className="ds-card loadtest-results">
      <div className="loadtest-metric-grid">
        <Metric label="Total requests" value={summary.total_requests} />
        <Metric label="Successful" value={summary.successful} tone="good" />
        <Metric label="Failed" value={summary.failed} tone={summary.failed > 0 ? "bad" : undefined} />
        <Metric label="Error rate" value={`${(summary.error_rate * 100).toFixed(1)}%`} />
        <Metric label="Wall time" value={`${summary.wall_seconds.toFixed(2)}s`} />
        <Metric label="Throughput" value={`${summary.throughput_rps.toFixed(2)} req/s`} />
        <Metric
          label="Requested concurrency"
          value={summary.requested_concurrency}
        />
        <Metric
          label="Realized concurrency"
          value={summary.realized_concurrency}
          tone={concurrencyLow ? "warn" : "good"}
        />
      </div>

      {concurrencyLow && (
        <div className="ds-alert ds-alert--warning">
          Realized concurrency ({summary.realized_concurrency}) is lower than requested (
          {summary.requested_concurrency}) — requests may be serialized somewhere (a lock, a single
          connection pool, or single-threaded downstream work).
        </div>
      )}

      <div className="loadtest-latency">
        <span className="ds-label">Latency (successful, s)</span>
        <span>min {lat.min.toFixed(2)}</span>
        <span>median {lat.median.toFixed(2)}</span>
        <span>mean {lat.mean.toFixed(2)}</span>
        <span>p95 {lat.p95.toFixed(2)}</span>
        <span>p99 {lat.p99.toFixed(2)}</span>
        <span>max {lat.max.toFixed(2)}</span>
      </div>

      <PerfStatsRow
        label="Time to first token (s)" stats={summary.ttft_seconds}
        sampleSize={summary.ttft_sample_size} decimals={2}
      />
      <PerfStatsRow
        label="Tokens / sec" stats={summary.tokens_per_second}
        sampleSize={summary.tokens_per_second_sample_size} decimals={1}
      />

      {summary.saved_as && <div className="loadtest-saved-note">Saved as {summary.saved_as}</div>}

      {Object.keys(summary.errors || {}).length > 0 && (
        <div className="loadtest-errors">
          {Object.entries(summary.errors).map(([msg, count]) => (
            <div key={msg} className="loadtest-error-row">
              <span className="loadtest-error-count">{count}x</span>
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccuracyResultsPanel({ summary, caseResults }) {
  return (
    <div className="ds-card loadtest-results">
      {summary && (
        <>
          <div className="loadtest-metric-grid">
            <Metric label="Total cases" value={summary.total_cases} />
            <Metric label="Passed" value={summary.passed} tone="good" />
            <Metric
              label="Failed" value={summary.total_cases - summary.passed}
              tone={summary.total_cases - summary.passed > 0 ? "bad" : undefined}
            />
            <Metric label="Pass rate" value={`${(summary.pass_rate * 100).toFixed(1)}%`} />
          </div>
          <PerfStatsRow
            label="Time to first token (s)" stats={summary.ttft_seconds}
            sampleSize={summary.ttft_sample_size} decimals={2}
          />
          <PerfStatsRow
            label="Tokens / sec" stats={summary.tokens_per_second}
            sampleSize={summary.tokens_per_second_sample_size} decimals={1}
          />
          {summary.saved_as && <div className="loadtest-saved-note">Saved as {summary.saved_as}</div>}
        </>
      )}

      {caseResults.length > 0 && (
        <div className="loadtest-case-table-wrap">
          <table className="ds-table loadtest-case-table">
            <thead>
              <tr>
                <th>ID</th><th>Group</th><th>Status</th><th>Retrieval</th><th>Context</th>
                <th>Answer</th><th>TTFT (s)</th><th>Tok/s</th>
              </tr>
            </thead>
            <tbody>
              {caseResults.map((r) => (
                <tr key={r.id} className={r.overall_pass ? "loadtest-case-pass" : "loadtest-case-fail"}>
                  <td>{r.id}</td>
                  <td>{r.group}</td>
                  <td>
                    <span className={`ds-badge ${r.overall_pass ? "ds-badge--success" : "ds-badge--danger"}`}>
                      {r.overall_pass ? "PASS" : "FAIL"}
                    </span>
                  </td>
                  <td>{r.has_scoring_signal ? `${(r.retrieval_score * 100).toFixed(0)}%` : "N/A"}</td>
                  <td>{r.has_scoring_signal ? `${(r.context_score * 100).toFixed(0)}%` : "N/A"}</td>
                  <td>{r.has_scoring_signal ? `${(r.answer_score * 100).toFixed(0)}%` : "N/A"}</td>
                  <td>{r.ttft_seconds != null ? r.ttft_seconds.toFixed(2) : "–"}</td>
                  <td>{r.tokens_per_second != null ? r.tokens_per_second.toFixed(1) : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className={`loadtest-metric${tone ? ` loadtest-metric-${tone}` : ""}`}>
      <span className="ds-stat-value loadtest-metric-value">{value}</span>
      <span className="ds-label">{label}</span>
    </div>
  );
}
